/**
 * Room state views: plain interfaces mirroring the server Colyseus Schema (apps/server/src/rooms/
 * schema/*), so the client can type the reflected state it reads. Field names and value types must
 * match the Schema exactly (the server asserts it at compile time).
 */
import type { GameModeKind } from './messages';
import type { PauseView } from './pause';
import type { Topology } from './topology';
import type { Platform, WorldId } from './types';

export interface PlayerView {
  sessionId: string;
  /** Small integer used in pose batches. */
  slot: number;
  name: string;
  color: string;
  /** Comma separated cosmetic ids. */
  cosmetics: string;
  platform: Platform;
  hp: number;
  alive: boolean;
  infected: boolean;
  score: number;
  bot: boolean;
  playerId: string;
  /** At 0 HP but alive, waiting for a revive (§2.11). */
  downed: boolean;
  /** Server time (ms) at which a downed player bleeds out (0 when not downed). */
  downedUntil: number;
  /** Watching only (joined mid-round, or bled out under a 'spectate' DeathPolicy). */
  spectator: boolean;
  /** Avatar id (avatarId.ts), '' = the default avatar ('gt'). Since protocol v4. */
  avatar: string;
  /** Game look (avatarLook.ts encodeAvatarLook), '' = none. Since protocol v6. */
  look: string;
  /**
   * Away: the player's headset / tab lost focus (system menu, headset off, tab hidden; away.ts).
   * Optional for the views tests and bots built by hand; the room's state always has it.
   */
  away?: boolean;
  /** Voice group, '' = ungrouped (written by the room plugin, voice.setGroup). */
  voiceGroup?: string;
  /**
   * Connection lost: the seat is kept for the reconnect grace (RECONNECT_GRACE_S), cleared on return.
   * Optional for the views tests and bots built by hand; the room's state always has it.
   */
  dropped?: boolean;
}


export interface NpcView {
  id: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  hp: number;
  maxHp: number;
  /** 'idle' | 'chase' | 'attack' | 'dead' */
  state: string;
  target: string;
}

export interface ModeView {
  kind: GameModeKind;
  /** 'idle' | 'countdown' | 'running' | 'ended' */
  phase: string;
  timeLeft: number;
  round: number;
  /** sessionId of the winner(s) at the end, comma separated. */
  winners: string;
  /**
   * sessionId of the player who started the current round, '' when no round is active. While a
   * round is busy only this player may stop it (unless they left), so clients show Stop to them.
   */
  startedBy: string;
}

export interface WorldStateView {
  world: WorldId;
  code: string;
  isPublic: boolean;
  players: Map<string, PlayerView>;
  mode: ModeView;
  serverTime: number;
  /**
   * Room pause (pause.ts). Always sent by today's server; optional so a fake state (tests) or an older
   * server reads as « not paused » (`isPaused(state.pause)`).
   */
  pause?: PauseView;
}

/** One player's envelope in a room of a game that lists `playerEnvelopes`: an index into `playerEnvelopesFor(world)`. */
export interface PlayerEnvelopeView {
  index: number;
}

/** The extra field of a room state built for an opting game (not on WorldStateView, whose keys mirror WorldState); read by casting. */
export interface EnvelopesStateView {
  /** By sessionId. */
  envelopes?: ReadonlyMap<string, PlayerEnvelopeView>;
}

/** A vehicle of the room (§2.13). Seats and cargo are CSV strings to keep the Schema flat. */
export interface VehicleView {
  /** Decimal string of the u16 network id used by VehState records and SEATED poses. */
  id: string;
  /** VehicleDef kind (GameKey or 'core.cart'). */
  kind: string;
  /** sessionId of the driver (simulation owner), '' when nobody drives. */
  owner: string;
  /** Occupant sessionId per seat index, comma separated ('' for a free seat): "abc,,def". */
  seats: string;
  /** Object ids attached to the cargo box, comma separated. */
  cargo: string;
}

export interface PartyMemberView {
  sessionId: string;
  name: string;
  color: string;
  world: string;
  /** This member's sessionId in their current WORLD room ('' if none), verified by the server. */
  worldSession: string;
  /** Announced microphone state (PartyMicState in messages.ts; '' = not announced). Optional: additive. */
  mic?: string;
  /** Announced walkie channel (0 = whole party, 1..3 = a numbered channel). Optional: additive. */
  walkieChannel?: number;
}

export interface PartyStateView {
  code: string;
  members: Map<string, PartyMemberView>;
}

/**
 * HTTP room lookup (plain HTTP next to matchmaking, under the same /colyseus prefix):
 *   GET {endpoint}/rooms/{CODE} → RoomLookupView, or null when no room has that code
 *   GET {endpoint}/rooms/new    → { code } a fresh code no live room uses
 * Lets "Join with a code" refuse a typo instead of silently creating an empty room, and land the
 * joiner directly in the world where the group currently is.
 */
export const ROOMS_HTTP_PATH = '/rooms';

export interface RoomLookupView {
  code: string;
  /** World of the fullest world room with that code, or null when only the party room exists (travelling). */
  world: WorldId | null;
  /** Players currently in the group's world rooms. */
  players: number;
  /** True when every world room with that code is full. */
  full: boolean;
  /**
   * Only with `?world=<id>` (a registered world): the seats of the code's zone-less room of that world, or null
   * when the code has none. `full` = locked or at capacity. Absent without `world`.
   */
  room?: { players: number; capacity: number; full: boolean } | null;
}

export interface NewRoomCodeView {
  code: string;
}

/**
 * The tail of the room list route under ROOMS_HTTP_PATH (engine 0.5, v05-rooms-list-e):
 *   GET {endpoint}/rooms/list?world=&tags=a,b&device=vr&joinable=1&limit= → RoomListing[] (types.ts), the
 *   listed rooms matching the filter (RoomListFilter, `filterListings` semantics), fullest first, at most
 *   ROOM_LIST_MAX. Never a room code: 'list' uses letters the code alphabet leaves out (L, I).
 */
export const ROOM_LIST_HTTP_TAIL = 'list';

/**
 * `GET {endpoint}/voice/ice` → IceServersView (engine 0.5, E13): the ICE servers a client gives its WebRTC
 * peer connections (voice mesh, Host-topology links). STUN from the server's ICE_STUN_URLS (default the two
 * STUN servers the client uses today) and, when the server runs a coturn with `use-auth-secret`, one TURN
 * entry with short-lived REST credentials. Never cached; an unconfigured server answers STUN only.
 */
export const VOICE_ICE_HTTP_PATH = '/voice/ice';

/** One RTCIceServer as the server describes it (the shape of the DOM's RTCIceServer, without DOM types). */
export interface IceServerView {
  urls: string[];
  /** TURN only: `<expiry unix seconds>:<sessionId>` (coturn REST credentials). */
  username?: string;
  /** TURN only: base64 HMAC-SHA1 of `username` with the server's TURN secret. */
  credential?: string;
}

export interface IceServersView {
  iceServers: IceServerView[];
  /** Seconds the TURN credentials stay valid (21600 = 6 h); a client refetches after that. */
  ttl: number;
}

/**
 * `POST {endpoint}/voice/livekit-token`, body `{ ticket }` → LiveKitTokenView (engine 0.5, E13): exchanges a voice
 * ticket the party room handed its member (Msg.VoiceTicket, single-use, 60 s) for a LiveKit access token of the
 * party's voice room (`party:<code>`), valid 10 min. 503 `{ error: 'livekit_unconfigured' }` when the server has no
 * LIVEKIT_URL / LIVEKIT_API_KEY / LIVEKIT_API_SECRET (the client keeps the P2P mesh), 400 on a malformed body,
 * 403 on an unknown, expired, used or revoked ticket. Never cached.
 */
export const VOICE_LIVEKIT_TOKEN_HTTP_PATH = '/voice/livekit-token';

/** Answer of `POST {endpoint}/voice/livekit-token`. */
export interface LiveKitTokenView {
  /** HS256 JWT signed with the server's LiveKit API secret (`sub` = the party session id). */
  token: string;
  /** The LiveKit server URL to connect to (the server's LIVEKIT_URL). */
  url: string;
}

/**
 * Device identity (engine 0.5, v05-identity-e): plain HTTP next to matchmaking, under the same /colyseus prefix.
 *   POST {endpoint}/identity/anonymous  body `{ previousId? }` → AnonymousIdentityView
 *   POST {endpoint}/identity/verify     body `{ token }` → IdentityVerifyView
 * The server issues a playerId and a device token (32 random bytes, base64url) and keeps only the token's
 * SHA-256 hash; the client keeps the token and sends it in its join options so the room can verify the id.
 */
export const IDENTITY_HTTP_PATH = '/identity';
/** The tail of the anonymous-identity route under IDENTITY_HTTP_PATH: `/identity/anonymous`. */
export const IDENTITY_ANONYMOUS_HTTP_TAIL = 'anonymous';
/**
 * The tail of the credential check under IDENTITY_HTTP_PATH: `POST /identity/verify`, body `{ token }` →
 * IdentityVerifyView. A client checks the credential it kept before it joins with it.
 */
export const IDENTITY_VERIFY_HTTP_TAIL = 'verify';
/** An identity token: 32 bytes in base64url without padding (43 characters). */
export const IDENTITY_TOKEN = /^[A-Za-z0-9_-]{43}$/;
/** A player id as rooms accept it (join options): 1 to 64 letters, digits, `_` or `-`. */
export const IDENTITY_PLAYER_ID = /^[a-z0-9_-]{1,64}$/i;

/** Answer of `POST {endpoint}/identity/anonymous`: the issued id and the device token (shown once). */
export interface AnonymousIdentityView {
  playerId: string;
  token: string;
}

/** Answer of `POST {endpoint}/identity/verify`: the token's playerId, null when this server does not know it. */
export interface IdentityVerifyView {
  playerId: string | null;
}

/**
 * Server-side saves (engine 0.5, v05-save-server-e): plain HTTP next to matchmaking, under the same /colyseus prefix.
 *   GET {endpoint}/saves/<scope>  → 200 SaveDocView, 404 `{ error: 'none' }` when nothing is stored
 *   PUT {endpoint}/saves/<scope>  body SaveDocView → 200 SavePutView, 409 SaveConflictView (an older updatedAt)
 * Both need `Authorization: Bearer <identity token>` (401 without one, or with a token the server does not know):
 * a player reads and writes only the saves of its verified playerId. Never cached.
 */
export const SAVES_HTTP_PATH = '/saves';
/** The economy save's scope (the wallet, inventory and unlocks of SaveBackend). */
export const SAVE_ECONOMY_SCOPE = 'economy';
/**
 * A save scope: `economy` or `game.<id>` (a Game Pack id, GAME_ID_PATTERN). It is a URL segment and a file name on
 * the server: nothing else (no dot segment, no separator) is ever accepted.
 */
export const SAVE_SCOPE = /^(?:economy|game\.[a-z][a-z0-9]{0,63})$/;
/** Largest PUT body of one scope, in UTF-8 bytes of its JSON (`{ updatedAt, data }`): 413 above. */
export const SAVE_SCOPE_MAX_BYTES = 256 * 1024;
/** Distinct scopes the server keeps per player: a new scope beyond it is refused (409 `too_many_scopes`). */
export const SAVE_MAX_SCOPES = 64;

/** A save scope (SAVE_SCOPE). */
export type SaveScope = typeof SAVE_ECONOMY_SCOPE | `game.${string}`;

/** The save scope of a game's GameSave: `game.<id>`. */
export function gameSaveScope(id: string): SaveScope {
  return `game.${id}`;
}

/** One stored save: its JSON data and when it was last written (ms since epoch; last writer wins). */
export interface SaveDocView {
  updatedAt: number;
  data: unknown;
}

/** Answer of an accepted `PUT {endpoint}/saves/<scope>`: the updatedAt stored (clamped near server time). */
export interface SavePutView {
  ok: true;
  updatedAt: number;
}

/** Answer (409) of a `PUT {endpoint}/saves/<scope>` older than the stored save: the stored save, to adopt. */
export interface SaveConflictView {
  ok: false;
  error: 'conflict';
  stored: SaveDocView;
}
